import { Link, useLocation } from "react-router-dom";
import { sizedImage } from "@/lib/imageUtils";
import { useState, useEffect } from "react";
import { Menu, X, Phone, CalendarCheck, UtensilsCrossed, BedDouble, ChevronDown, ArrowRight, MessageCircle, Search } from "lucide-react";
import SiteSearch from "./SiteSearch";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import LanguageSwitcher from "./LanguageSwitcher";
import api, { normalizeImageUrl } from "@/lib/api";
import { useSiteContent, sectionImage } from "@/lib/siteContent";
import { ROOMS_BOOKING_URL, whatsappLink } from "@/lib/contacts";

const useBookingActions = (t, lang) => [
  { key: "experience", to: "/esperienze", icon: CalendarCheck, label: lang === "it" ? "Prenota esperienza" : "Book an experience", img: "/esperienze" },
  { key: "table", to: "/ristorante-agricolo", icon: UtensilsCrossed, label: t("nav.bookTable"), img: "/ristorante-agricolo" },
  { key: "room", href: ROOMS_BOOKING_URL, icon: BedDouble, label: t("nav.bookRooms"), img: "/soggiorni" },
];

const MobileMenu = ({ links, actions, onClose, isActive, content, logoUrl, t }) => (
  <div className="fixed inset-0 z-[80] bg-[#faf7f2] overflow-y-auto mobile-menu-sheet xl:hidden" data-testid="mobile-menu">
    <div className="flex items-center justify-between h-16 px-4 border-b border-[#E5E0D8] sticky top-0 bg-[#faf7f2]/95 backdrop-blur-md">
      <img src={logoUrl || "/favicon.png"} alt="Agriturismo Sadira" className="h-10 w-auto" style={{ filter: logoUrl ? "none" : "brightness(0)" }} />
      <button onClick={onClose} className="w-11 h-11 rounded-full bg-white border border-[#E5E0D8] flex items-center justify-center" aria-label="Chiudi menu" data-testid="mobile-menu-close">
        <X className="w-5 h-5" />
      </button>
    </div>
    <div className="px-4 py-6 space-y-8">
      <div className="grid grid-cols-2 gap-3" data-testid="mobile-quick-actions">
        {actions.map((a, i) => {
          const img = sizedImage(normalizeImageUrl(sectionImage(content, a.img)), 800);
          const inner = (
            <div className={`relative h-28 rounded-2xl overflow-hidden ${img ? "" : "bg-[#556B2F]"} mobile-menu-item`} style={{ animationDelay: `${i * 50}ms` }}>
              {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-black/10" />
              <div className="absolute bottom-0 inset-x-0 p-3 text-white">
                <a.icon className="w-5 h-5 mb-1" />
                <span className="text-sm font-semibold leading-tight block">{a.label}</span>
              </div>
            </div>
          );
          return a.href ? (
            <a key={a.key} href={a.href} target="_blank" rel="noopener noreferrer" onClick={onClose} data-testid={`mobile-action-${a.key}`}>{inner}</a>
          ) : (
            <Link key={a.key} to={a.to} onClick={onClose} data-testid={`mobile-action-${a.key}`}>{inner}</Link>
          );
        })}
        <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" onClick={onClose} data-testid="mobile-action-whatsapp">
          <div className="relative h-28 rounded-2xl overflow-hidden bg-[#25D366] mobile-menu-item" style={{ animationDelay: "150ms" }}>
            <div className="absolute bottom-0 inset-x-0 p-3 text-white">
              <MessageCircle className="w-5 h-5 mb-1" />
              <span className="text-sm font-semibold leading-tight block">WhatsApp</span>
            </div>
          </div>
        </a>
      </div>

      <nav className="flex flex-col">
        {links.map((link, i) => {
          const img = sizedImage(normalizeImageUrl(sectionImage(content, link.href) || (link.href === "/" ? content?.homepage_hero?.image : link.href === "/azienda-agricola" ? content?.farm_production?.hero_image : "")), 480);
          return (
            <Link
              key={link.href}
              to={link.href}
              onClick={onClose}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
              className={`mobile-menu-item flex items-center gap-4 py-3 border-b border-[#E5E0D8] ${isActive(link.href) ? "text-[#556B2F]" : "text-[#2C2C2C]"}`}
              style={{ animationDelay: `${200 + i * 40}ms` }}
            >
              <span className="w-12 h-12 rounded-xl overflow-hidden bg-[#dbe3c8] flex-shrink-0">
                {img && <img src={img} alt="" loading="lazy" className="w-full h-full object-cover" />}
              </span>
              <span className="flex-1 text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{link.label}</span>
              <ArrowRight className="w-5 h-5 opacity-40" />
            </Link>
          );
        })}
      </nav>

      <div className="flex items-center justify-between gap-3 pb-6">
        <Link to="/contatti" onClick={onClose} className="flex-1">
          <Button className="w-full rounded-full h-12 bg-[#556B2F] text-white hover:bg-[#445725]">
            <Phone className="w-4 h-4 mr-2" /> {t("common.contact")}
          </Button>
        </Link>
        <LanguageSwitcher />
      </div>
    </div>
  </div>
);

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const content = useSiteContent();
  const actions = useBookingActions(t, i18n.language);

  const navLinks = [
    { href: "/", label: t('common.home') },
    { href: "/negozio", label: t('common.shop') },
    { href: "/esperienze", label: t('common.events') },
    { href: "/didattica", label: t('common.education') },
    { href: "/ristorante-agricolo", label: t('common.farmhouse') },
    { href: "/azienda-agricola", label: t('common.farm') },
    { href: "/soggiorni", label: t('common.stays') },
    { href: "/chi-siamo", label: t('common.about') },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    api.get("/admin/images?category=logo").then((res) => {
      if (res.data?.length > 0) setLogoUrl(res.data[0].url);
    }).catch(() => {});
  }, []);

  useEffect(() => setIsOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    document.body.classList.toggle("menu-open", isOpen);
    return () => { document.body.style.overflow = ""; document.body.classList.remove("menu-open"); };
  }, [isOpen]);

  const isActive = (path) => location.pathname === path;
  const solid = scrolled || location.pathname !== "/";

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,height] duration-300 ${
        solid ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06),0_8px_24px_-12px_rgba(0,0,0,0.12)]" : "bg-transparent"
      }`} data-testid="navbar" data-compact={scrolled ? "true" : "false"}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className={`flex items-center justify-between gap-4 transition-[height] duration-300 ${scrolled ? "h-16" : "h-20"}`}>
            <button
              className={`xl:hidden w-11 h-11 -ml-2 rounded-full flex items-center justify-center transition-colors ${solid ? "text-[#2C2C2C] hover:bg-black/5" : "text-white hover:bg-white/15"}`}
              onClick={() => setIsOpen(true)}
              aria-label="Apri menu"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="hidden xl:flex items-center gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`nav-link relative px-2.5 2xl:px-4 py-2 text-sm whitespace-nowrap font-medium rounded-full transition-colors duration-200 ${
                    isActive(link.href)
                      ? solid ? "text-[#556B2F] is-active" : "text-white is-active"
                      : solid ? "text-[#2C2C2C] hover:text-[#556B2F]" : "text-white/90 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-1.5 md:gap-3">
              <button
                onClick={() => setSearchOpen(true)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${solid ? "text-[#2C2C2C] hover:bg-black/5" : "text-white hover:bg-white/15"}`}
                aria-label="Cerca"
                data-testid="nav-search-button"
              >
                <Search className="w-5 h-5" />
              </button>
              <div className="hidden lg:block"><LanguageSwitcher variant="minimal" /></div>
              <Link to="/contatti" data-testid="nav-contatti" className="hidden 2xl:block">
                <Button variant="ghost" className={`rounded-full px-4 ${solid ? "text-[#2C2C2C] hover:bg-black/5" : "text-white hover:bg-white/15"}`}>
                  <Phone className="w-4 h-4 mr-2" /> {t('common.contact')}
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className={`rounded-full px-4 md:px-5 h-10 shadow-md ${solid ? "bg-[#556B2F] text-white hover:bg-[#445725]" : "bg-white text-[#556B2F] hover:bg-white/90"}`} data-testid="nav-book-button">
                    {i18n.language === "it" ? "Prenota" : "Book"} <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 rounded-2xl p-2">
                  {actions.map((a) => (
                    <DropdownMenuItem key={a.key} asChild className="rounded-xl py-2.5 cursor-pointer">
                      {a.href ? (
                        <a href={a.href} target="_blank" rel="noopener noreferrer" data-testid={`nav-book-${a.key}`}>
                          <a.icon className="w-4 h-4 mr-2 text-[#556B2F]" /> {a.label}
                        </a>
                      ) : (
                        <Link to={a.to} data-testid={`nav-book-${a.key}`}>
                          <a.icon className="w-4 h-4 mr-2 text-[#556B2F]" /> {a.label}
                        </Link>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/" className="flex items-center" data-testid="navbar-logo">
                <img
                  src={logoUrl || "/favicon.png"}
                  alt="Agriturismo Sadira"
                  className={`w-auto transition-[height] duration-300 ${scrolled ? "h-9 md:h-10" : "h-10 md:h-12"}`}
                  style={{ filter: logoUrl ? 'none' : 'brightness(0)' }}
                />
              </Link>
            </div>
          </div>
        </div>
      </nav>
      {searchOpen && <SiteSearch onClose={() => setSearchOpen(false)} isIt={i18n.language === "it"} />}
      {isOpen && (
        <MobileMenu links={navLinks} actions={actions} onClose={() => setIsOpen(false)} isActive={isActive} content={content} logoUrl={logoUrl} t={t} />
      )}
    </>
  );
};

export default Navbar;
